import { useCallback, useMemo } from 'react'

import { useApolloClient } from '@apollo/client'
import { useQueryClient } from '@tanstack/react-query'
import {
  CreateFileMutation,
  CreateFileMutationVariables,
  GetAreFilesProcessedQuery,
  GetAreFilesProcessedQueryVariables,
  GetFilePageImageUrlsQuery,
  GetFilePageImageUrlsQueryVariables,
  GetFileUploadCredentialsMutation,
  GetFileUploadCredentialsMutationVariables,
  GetIsFileProcessedQuery,
  GetIsFileProcessedQueryVariables,
  GetFilesQuery,
  GetFilesQueryVariables,
  RenameFileMutation,
  RenameFileMutationVariables,
  RotateAllFilePagesMutation,
  RotateAllFilePagesMutationVariables,
  RotateFilePageMutation,
  RotateFilePageMutationVariables,
  GetRecordPlansSetContextForFilePageQuery,
  GetRecordPlansSetContextForFilePageQueryVariables,
} from 'types/graphql'

import { QueryKey, UseQueryOptions, useMutation, useQuery } from 'src/utils/queryClient'

const GetIsFileProcessed = gql`
  query GetIsFileProcessedQuery($id: Int!) {
    file(id: $id) {
      pageProcessingFinishedAt
      pages {
        ...FilePageFragment
      }
    }
  }
`

export const useIsFileProcessedQuery = (
  id: number,
  options?: UseQueryOptions<GetIsFileProcessedQuery, GetIsFileProcessedQueryVariables>
) => {
  return useQuery<GetIsFileProcessedQuery, GetIsFileProcessedQueryVariables>({
    queryKey: [QueryKey.FileProcessed, id],
    queryDocument: GetIsFileProcessed,
    variables: {
      id,
    },
    ...options,
  })
}
const GetAreFilesProcessed = gql`
  query GetAreFilesProcessedQuery($ids: [Int!]!) {
    files(ids: $ids) {
      pageProcessingFinishedAt
    }
  }
`

export const useAreFilesProcessedQuery = (
  ids: number[],
  options?: UseQueryOptions<GetAreFilesProcessedQuery, GetAreFilesProcessedQueryVariables>
) => {
  return useQuery<GetAreFilesProcessedQuery, GetAreFilesProcessedQueryVariables>({
    queryKey: [QueryKey.FileProcessed, ids],
    queryDocument: GetAreFilesProcessed,
    variables: {
      ids,
    },
    ...options,
  })
}

const GetFilePageImageUrls = gql`
  query GetFilePageImageUrlsQuery($id: Int!) {
    filePage(id: $id) {
      id
      signedSmallImageUrl
      signedMediumImageUrl
      signedLargeImageUrl
    }
  }
`

const ONE_MINUTE_IN_MS = 60 * 1000
export const useFilePageImageUrlsQuery = (
  id: number,
  options?: UseQueryOptions<GetFilePageImageUrlsQuery, GetFilePageImageUrlsQueryVariables>
) => {
  return useQuery<GetFilePageImageUrlsQuery, GetFilePageImageUrlsQueryVariables>({
    queryKey: [QueryKey.FilePageImageUrls, id],
    queryDocument: GetFilePageImageUrls,
    variables: {
      id,
    },
    staleTime: ONE_MINUTE_IN_MS,
    ...options,
  })
}

const RotateFilePage = gql`
  mutation RotateFilePageMutation($input: RotateFilePageInput!) {
    rotateFilePage(input: $input)
  }
`

export const useRotateFilePageMutation = () => {
  return useMutation<RotateFilePageMutation, RotateFilePageMutationVariables>({
    mutationDocument: RotateFilePage,
  })
}

const RotateAllFilePages = gql`
  mutation RotateAllFilePagesMutation($input: RotateAllFilePagesInput!) {
    rotateAllFilePages(input: $input)
  }
`

export const useRotateAllFilePagesMutation = () => {
  return useMutation<RotateAllFilePagesMutation, RotateAllFilePagesMutationVariables>({
    mutationDocument: RotateAllFilePages,
  })
}

const Create = gql`
  mutation CreateFileMutation($input: CreateFileInput!) {
    createFile(input: $input) {
      ...ThumbnailFileFragment
    }
  }
`
export const useCreateFileMutation = () => {
  return useMutation<CreateFileMutation, CreateFileMutationVariables>({
    mutationDocument: Create,
  })
}

const Rename = gql`
  mutation RenameFileMutation($id: Int!, $newName: String!) {
    renameFile(id: $id, newName: $newName) {
      id
    }
  }
`
export const useRenameFileMutation = () => {
  return useMutation<RenameFileMutation, RenameFileMutationVariables>({
    mutationDocument: Rename,
  })
}

const GetFileUploadCredentials = gql`
  mutation GetFileUploadCredentialsMutation($input: GetFileUploadCredentialsInput!) {
    getFileUploadCredentials(input: $input) {
      ...FileUploadCredentialsFragment
    }
  }
`

export const useGetFileUploadCredentialsMutation = () => {
  return useMutation<GetFileUploadCredentialsMutation, GetFileUploadCredentialsMutationVariables>({
    mutationDocument: GetFileUploadCredentials,
  })
}

const GetFiles = gql`
  query GetFilesQuery($ids: [Int!]!) {
    files(ids: $ids) {
      ...FileFragment
    }
  }
`

export const useGetFilesQuery = () => {
  const queryClient = useQueryClient()
  const client = useApolloClient()

  return useCallback(
    (ids: number[]) =>
      queryClient.fetchQuery<GetFilesQuery, GetFilesQueryVariables>({
        queryKey: [QueryKey.Files, ids],
        queryFn: ({ signal }) =>
          client
            .query<GetFilesQuery, GetFilesQueryVariables>({
              query: GetFiles,
              variables: {
                ids,
              },
              context: {
                fetchOptions: {
                  signal,
                },
              },
            })
            .then(({ data }) => data),
      }),
    [client, queryClient]
  )
}

const GetRecordPlansSetContextForFilePage = gql`
  query GetRecordPlansSetContextForFilePageQuery($filePageId: Int!, $recordPlansSetId: Int!) {
    filePage(id: $filePageId) {
      id
      pageNumber
      rotationDegrees

      previousFilePage(currentRecordPlansSetId: $recordPlansSetId) {
        id
        pageNumber
        rotationDegrees
      }
    }
    recordPlansSet(id: $recordPlansSetId) {
      id

      previousRecordPlansSets {
        id
        versionDisplayName
      }
    }
  }
`

export const useGetRecordPlansSetContextForFilePageQuery = (
  filePageId: number,
  recordPlansSetId: number
) => {
  const { data, ...rest } = useQuery<
    GetRecordPlansSetContextForFilePageQuery,
    GetRecordPlansSetContextForFilePageQueryVariables
  >({
    queryKey: [QueryKey.RecordPlansSetContextForFilePage, filePageId, recordPlansSetId],
    queryDocument: GetRecordPlansSetContextForFilePage,
    variables: { filePageId, recordPlansSetId },
  })
  const previousRecordPlansSets = useMemo(
    () => data?.recordPlansSet?.previousRecordPlansSets ?? [],
    [data?.recordPlansSet?.previousRecordPlansSets]
  )
  return {
    filePage: data?.filePage,
    previousRecordPlansSets,
    ...rest,
  }
}
